import {
  Box,
  AspectRatio,
  Image,
  Text,
  HStack,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Flex,
} from '@chakra-ui/react';
import { StarIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import type { AudioPlaylistDto } from '../types';
import { formatTotalDuration } from '../utils';

interface PlaylistCardProps {
  playlist: AudioPlaylistDto;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  onOpen: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

export function PlaylistCard({
  playlist,
  isFavorite,
  onToggleFavorite,
  onOpen,
  onDuplicate,
  onDelete,
}: PlaylistCardProps) {
  const { t } = useTranslation();
  const isAd = playlist.playlistType === 'advertisement';

  return (
    <Box
      role="group"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="lg"
      overflow="hidden"
      bg="white"
      transition="all 0.15s"
      _hover={{ borderColor: 'brand.300', shadow: 'md' }}
      cursor="pointer"
      onClick={onOpen}
      position="relative"
    >
      <AspectRatio ratio={1}>
        {playlist.thumbnailUrl ? (
          <Image src={playlist.thumbnailUrl} alt={playlist.name} objectFit="cover" />
        ) : (
          <Flex bgGradient="linear(to-br, brand.400, brand.600)" align="center" justify="center">
            <Text fontSize="4xl" fontWeight="800" color="whiteAlpha.900">
              {playlist.name.charAt(0).toUpperCase()}
            </Text>
          </Flex>
        )}
      </AspectRatio>

      {/* Overlay controls */}
      <HStack position="absolute" top={2} right={2} spacing={1}>
        <IconButton
          aria-label={t('playlists.favorite')}
          icon={<StarIcon />}
          size="sm"
          variant="solid"
          bg="whiteAlpha.800"
          color={isFavorite ? 'yellow.400' : 'gray.400'}
          _hover={{ bg: 'white' }}
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite();
          }}
        />
        <Menu>
          <MenuButton
            as={IconButton}
            aria-label={t('common.more', 'More')}
            icon={<Text fontSize="lg" lineHeight={1}>⋮</Text>}
            size="sm"
            variant="solid"
            bg="whiteAlpha.800"
            color="gray.700"
            _hover={{ bg: 'white' }}
            onClick={(e) => e.stopPropagation()}
          />
          <MenuList onClick={(e) => e.stopPropagation()}>
            <MenuItem onClick={onOpen}>{t('playlists.open')}</MenuItem>
            <MenuItem onClick={onDuplicate}>{t('playlists.duplicate')}</MenuItem>
            <MenuItem color="red.500" onClick={onDelete}>
              {t('playlists.delete')}
            </MenuItem>
          </MenuList>
        </Menu>
      </HStack>

      <Box p={3}>
        <HStack spacing={2} mb={1}>
          {isAd && (
            <Badge colorScheme="orange" fontSize="0.6rem">
              {t('playlists.typeAd')}
            </Badge>
          )}
          {playlist.source === 'library' && (
            <Badge colorScheme="purple" fontSize="0.6rem">
              {t('playlists.sourceLibrary')}
            </Badge>
          )}
        </HStack>
        <Text fontWeight="600" color="gray.800" noOfLines={1}>
          {playlist.name}
        </Text>
        <Text fontSize="sm" color="gray.500" mt={0.5}>
          {t('playlists.trackCountLabel', { count: playlist.trackCount })} ·{' '}
          {formatTotalDuration(playlist.totalDurationSeconds)}
        </Text>
      </Box>
    </Box>
  );
}

export default PlaylistCard;
